/* Field park: the CSV upload converts with the upload round (field-wrapper.md). */
import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Text, TextField } from '@main/complykit';
import { useImportControlAiCriteriaMutation } from '@main/graphql/features/AICriteriaControl.admin.generated';
import {
  useImportEvidenceMutation,
  useImportEvidenceToControlsMutation,
} from '@main/graphql/features/Evidence.admin.generated';
import { useImportPoliciesMutation } from '@main/graphql/features/Policies.admin.generated';
import { useImportReviewsMutation } from '@main/graphql/features/Reviews.admin.generated';
import { useImportRisksMutation } from '@main/graphql/features/Risks.admin.generated';
import { useImportTasksMutation } from '@main/graphql/features/Tasks.admin.generated';
import { useImportVendorsMutation } from '@main/graphql/features/Vendors.admin.generated';
import { useImportProgramMutation } from '@main/graphql/mutations/ImportProgram.admin.generated';
import { useGetProgramsQuery } from '@main/graphql/queries/GetPrograms.generated';
import { toError } from '@main/shared/utils';
import {
  errorToast,
  FileUpload,
  singleFileSchema,
  successToast,
  useLazyFileUpload,
} from '@main/ui';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

export enum ImportCsvDataType {
  PROGRAM = 'PROGRAM',
  VENDOR = 'VENDOR',
  POLICY = 'POLICY',
  RISK = 'RISK',
  EVIDENCE = 'EVIDENCE',
  EVIDENCE_TO_CONTROL = 'EVIDENCE_TO_CONTROL',
  CONTROL_AI_CRITERIA = 'CONTROL_AI_CRITERIA',
  TASK = 'TASK',
  REVIEW = 'REVIEW',
}

type ImportDataFromCsvModalProps = {
  orgId: string;
  email?: string;
  onModalClose: () => void;
  isModalOpen: boolean;
  importType: ImportCsvDataType;
};

const importCsvSchema = z.object({
  organizationId: z.string(),
  email: z.email('Enter a valid e-mail.').or(z.literal('')),
  sharepointFolder: z.string().max(200, 'Use 200 characters or fewer.').optional(),
  file: singleFileSchema(),
});

type ImportCsvType = z.infer<typeof importCsvSchema>;

export const ImportDataFromCsvModal: FC<ImportDataFromCsvModalProps> = ({
  orgId,
  email,
  onModalClose,
  isModalOpen,
  importType,
}) => {
  let saveRequest: SubmitHandler<ImportCsvType>;
  let modelHeaderText = '';
  let isHideSharepointInput = true;
  const [importVendors] = useImportVendorsMutation();
  const [importProgram] = useImportProgramMutation();
  const [importPolicies] = useImportPoliciesMutation();
  const [importRisks] = useImportRisksMutation();
  const [importTasks] = useImportTasksMutation();
  const [importEvidence] = useImportEvidenceMutation();
  const [importEvidenceToControls] = useImportEvidenceToControlsMutation();
  const [importControlAICriteria] = useImportControlAiCriteriaMutation();
  const [importReviews] = useImportReviewsMutation();
  const { refetch: refetchPrograms } = useGetProgramsQuery({ orgId });

  const fileUpload = useLazyFileUpload({
    onFileAdd: (file) => {
      setValue('file', file, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    onFileDelete: () => {
      setValue('file', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  });

  switch (importType) {
    case ImportCsvDataType.VENDOR: {
      isHideSharepointInput = true;
      modelHeaderText = 'Import vendors to organization';

      const saveRequestForVendor: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importVendors({
          object: {
            organizationId: orgId,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Vendors imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import vendors. Try again later.');
            datadogLogs.logger.error('Unable to import vendors through CSV.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForVendor;
      break;
    }
    case ImportCsvDataType.PROGRAM: {
      isHideSharepointInput = true;
      modelHeaderText = 'Import programs to organization';

      const saveRequestForProgram: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importProgram({
          object: {
            organizationId: orgId,
            sharepointFolder: data.sharepointFolder,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Program imported! You will receive an email with the results.');
            fileUpload.clear();
            refetchPrograms();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import program. Try again later.');
            datadogLogs.logger.error('Unable to import program.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForProgram;
      break;
    }
    case ImportCsvDataType.POLICY: {
      isHideSharepointInput = false;
      modelHeaderText = 'Import policies to organization';

      const saveRequestForPolicy: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importPolicies({
          object: {
            organizationId: orgId,
            sharepointFolder: data.sharepointFolder,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Policies imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import policies. Try again later.');
            datadogLogs.logger.error('Unable to import policies.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForPolicy;
      break;
    }
    case ImportCsvDataType.RISK: {
      isHideSharepointInput = false;
      modelHeaderText = 'Import risks to organization';

      const saveRequestForRisk: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importRisks({
          object: {
            organizationId: orgId,
            sharepointFolder: data.sharepointFolder,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Risks imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import risks. Try again later.');
            datadogLogs.logger.error('Unable to import risks through CSV.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForRisk;
      break;
    }
    case ImportCsvDataType.EVIDENCE: {
      isHideSharepointInput = false;
      modelHeaderText = 'Import evidence to controls';

      const saveRequestForEvidence: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importEvidence({
          object: {
            organizationId: orgId,
            sharepointFolder: data.sharepointFolder,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Evidence imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import evidence. Try again later.');
            datadogLogs.logger.error('Unable to import evidence.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForEvidence;
      break;
    }
    case ImportCsvDataType.EVIDENCE_TO_CONTROL: {
      isHideSharepointInput = true;
      modelHeaderText = 'Connect control with evidence';

      const saveRequestForEvidenceControls: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importEvidenceToControls({
          object: {
            organizationId: orgId,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast(
              'Control to evidence connection imported! You will receive an email with the results.',
            );
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import control to evidence connection. Try again later.');
            datadogLogs.logger.error(
              'Unable to import control to evidence connection.',
              {},
              toError(error),
            );
          });
      };

      saveRequest = saveRequestForEvidenceControls;
      break;
    }
    case ImportCsvDataType.CONTROL_AI_CRITERIA: {
      isHideSharepointInput = true;
      modelHeaderText = 'Import control AI criteria';

      const saveAICriteriaForControl: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importControlAICriteria({
          object: {
            organizationId: orgId,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('AI criteria imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error: Error) => {
            errorToast('Unable to import AI criteria. Try again later.');
            datadogLogs.logger.error(
              'Unable to import AI criteria for controls.',
              {},
              toError(error),
            );
          });
      };

      saveRequest = saveAICriteriaForControl;
      break;
    }
    case ImportCsvDataType.TASK: {
      isHideSharepointInput = true;
      modelHeaderText = 'Import tasks to organization';

      const saveRequestForTask: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importTasks({
          object: {
            organizationId: orgId,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Tasks imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import tasks. Try again later.');
            datadogLogs.logger.error('Unable to import tasks through CSV.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForTask;
      break;
    }
    case ImportCsvDataType.REVIEW: {
      isHideSharepointInput = false;
      modelHeaderText = 'Import reviews to organization';

      const saveRequestForReviews: SubmitHandler<ImportCsvType> = async (data) => {
        const uploadedFileMeta = await fileUpload.upload();
        if (uploadedFileMeta.error) {
          errorToast('Error in uploading file.');
          return;
        }

        await importReviews({
          object: {
            organizationId: orgId,
            sharepointFolder: data.sharepointFolder,
            email: data.email,
            fileId: uploadedFileMeta.file.id,
          },
        })
          .unwrap()
          .then(() => {
            successToast('Reviews imported! You will receive an email with the results.');
            fileUpload.clear();
            reset();
            onModalClose();
          })
          .catch((error) => {
            errorToast('Unable to import reviews. Try again later.');
            datadogLogs.logger.error('Unable to import reviews through CSV.', {}, toError(error));
          });
      };

      saveRequest = saveRequestForReviews;
      break;
    }
    default: {
      throw new Error(`Unknown import type`);
    }
  }

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ImportCsvType>({
    resolver: zodResolver(importCsvSchema),
    defaultValues: {
      organizationId: orgId,
      sharepointFolder: '',
      email: email ?? '',
      file: undefined,
    },
    shouldFocusError: false,
  });

  return (
    <Modal
      size="xl"
      isCentered
      autoFocus={false}
      isOpen={isModalOpen}
      onClose={onModalClose}
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody pt={4} pb={0}>
          <form onSubmit={handleSubmit(saveRequest)} noValidate>
            <Stack spacing={6} pb={4}>
              <Text size="lg" weight="bold">
                {modelHeaderText}
              </Text>

              <Field label="CSV file" error={errors.file?.message}>
                {(fieldControl) => (
                  <FileUpload {...fileUpload.props}>
                    <FileUpload.Files />
                    <FileUpload.Dropzone inputProps={fieldControl} />
                  </FileUpload>
                )}
              </Field>

              {!isHideSharepointInput && (
                <Controller
                  control={control}
                  name="sharepointFolder"
                  render={({ field, fieldState }) => (
                    <TextField
                      ref={field.ref}
                      label="Sharepoint folder"
                      name={field.name}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Enter the ComplyanceMigration folder name inside where the files are stored"
                      error={fieldState.error?.message}
                    />
                  )}
                />
              )}

              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <TextField
                    ref={field.ref}
                    type="email"
                    label="E-mail to receive results"
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Type your email to receive the results of the import"
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Flex gap={2} justify="end">
                <Button variant="soft" isDisabled={isSubmitting} onPress={() => onModalClose()}>
                  Cancel
                </Button>
                <Button isPending={isSubmitting} type="submit">
                  Submit
                </Button>
              </Flex>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
